import React from "react";
import { NavLink } from "react-router-dom";
import { ShieldAlert, Crosshair, Radar, ScanLine, Code2, LogOut, Image as ImageIcon, Palette, Target, Eye, Mail, Users, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";

const baseTabs = [
  { to: "/operator", end: true, label: "Lawsuit Radar", icon: Radar, testid: "tab-lawsuit-radar" },
  { to: "/operator/geo-blitz", label: "Geo-Blitz Console", icon: Crosshair, testid: "tab-geo-blitz" },
  { to: "/operator/auditor", label: "Site Auditor & Pitch Lab", icon: ScanLine, testid: "tab-site-auditor" },
  { to: "/operator/deployments", label: "Script Deployment", icon: Code2, testid: "tab-script-deployment" },
  { to: "/operator/images", label: "Image Manager", icon: ImageIcon, testid: "tab-image-manager" },
  { to: "/operator/competitor-hunt", label: "Competitor Hunt", icon: Target, testid: "tab-competitor-hunt" },
  { to: "/operator/inbound", label: "Inbound Scans", icon: Eye, testid: "tab-inbound-scans" },
  { to: "/operator/leads", label: "Inbound Leads", icon: Mail, testid: "tab-inbound-leads" },
  { to: "/operator/brand", label: "Brand & Identity", icon: Palette, testid: "tab-brand-settings" },
  { to: "/operator/account", label: "Account & Security", icon: KeyRound, testid: "tab-account-security" },
];

// Owner-only tabs (hidden from partner accounts).
const ownerOnlyTabs = [
  { to: "/operator/team", label: "Team", icon: Users, testid: "tab-team" },
];

export default function Sidebar() {
  const { email, role, logout } = useAuth();
  const tabs = role === "owner" ? [...baseTabs, ...ownerOnlyTabs] : baseTabs;

  return (
    <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-zinc-950 text-white border-r border-zinc-950 print:hidden">
      <div className="p-5 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-9 h-9 border-2 border-white grid place-items-center">
          <ShieldAlert className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-400">
            Internal
          </div>
          <div className="font-display text-base font-black tracking-tight">ADA INTERCEPTOR</div>
        </div>
      </div>

      <nav className="flex-1 py-4">
        <div className="label-xs px-5 mb-2 text-zinc-500">Operator Modules</div>
        {tabs.map(({ to, end, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors duration-150 border-l-4 ${
                isActive
                  ? "border-blue-500 bg-zinc-900 text-white font-bold"
                  : "border-transparent text-zinc-400 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} />
            <span className="tracking-tight">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-zinc-800 p-4">
        <div className="label-xs text-zinc-500 mb-1 flex items-center justify-between">
          <span>Operator</span>
          <span
            data-testid="sidebar-operator-role"
            className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 border ${
              role === "owner"
                ? "border-emerald-500 text-emerald-400"
                : "border-amber-500 text-amber-400"
            }`}
          >
            {role || "owner"}
          </span>
        </div>
        <div className="font-mono text-xs text-zinc-200 mb-3 truncate" data-testid="sidebar-operator-email">
          {email || "—"}
        </div>
        <button
          onClick={logout}
          data-testid="sidebar-logout-button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-zinc-700 text-zinc-200 text-xs font-bold uppercase tracking-wider hover:bg-red-600 hover:border-red-600 transition-colors duration-150"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );
}
